import axios from 'axios';

import { getUserData, ISOBSERVER, ISSUPPORTER, VESRAPI } from '../constants/global';

// VESR token acquisition. Staging api.vesr validates issuer/key strictly and
// rejects the raw core JWT with 401 (see the admin app's vesr-auth.ts — same
// finding), so we exchange the core JWT for a VESR-issued token via
// POST /api/v1/vesr/auth/token (available where the service runs in its
// Development environment, i.e. staging). Where that endpoint is absent
// (production), callers fall back to the core JWT, which production VESR
// normalizes via CoreTokenClaimsNormalizer. Mirrors the admin pattern; cached
// in localStorage with expiry + single-flight acquisition.

const VESR_TOKEN_KEY = 'vesrAgentToken';
const VESR_TOKEN_EXPIRY_KEY = 'vesrAgentTokenExpiresAt';
const CLOCK_SKEW_BUFFER_S = 60;

/**
 * Mirrors the backend's CoreTokenClaimsNormalizer mapping for the exchange: admin roles →
 * SystemAdmin, Party Agent → FieldOfficer (required to create incidents), everyone else →
 * Observer. Independent observers never come through here: see resolveVesrBearer.
 */
const mapCoreRoleToVesrRole = (roles: string[]): string => {
	const norm = roles.map((r) => (r || '').trim().toLowerCase());
	if (norm.some((r) => ['super admin', 'party admin', 'candidate'].includes(r))) return 'SystemAdmin';
	if (norm.includes('party agent')) return 'FieldOfficer';
	return 'Observer';
};

export const getVesrToken = (): string | null => {
	try {
		return localStorage.getItem(VESR_TOKEN_KEY);
	} catch {
		return null;
	}
};

export const isVesrTokenValid = (): boolean => {
	try {
		const token = localStorage.getItem(VESR_TOKEN_KEY);
		const expiresAt = localStorage.getItem(VESR_TOKEN_EXPIRY_KEY);
		if (!token || !expiresAt) return false;
		const nowS = Math.floor(Date.now() / 1000);
		return nowS < Number(expiresAt) - CLOCK_SKEW_BUFFER_S;
	} catch {
		return false;
	}
};

let pendingAcquire: Promise<boolean> | null = null;

/**
 * Set once the exchange endpoint answers 404, which means this deployment does not expose it
 * (it is opt-in behind `Vesr:EnableDevTokenEndpoint` because it mints SuperAdmin tokens
 * unauthenticated). That answer is stable for the session, so re-asking is pure cost.
 *
 * <p>Single-flight alone does not cover this: it dedupes CONCURRENT callers, not sequential
 * ones. Support chat calls resolveVesrBearer() per operation AND from the socket's
 * accessTokenFactory on every reconnect, so without this every message and every retry on a
 * flaky link pays a doomed round trip first — on the 2G connections field agents actually
 * have. It also floods the console buffer that "Report a problem" ships to support, burying
 * the real diagnostics in 404s.</p>
 *
 * <p>Only a 404 is remembered. A network error or a 5xx is transient and must stay
 * retryable.</p>
 */
let exchangeUnavailable = false;

export const acquireVesrToken = async (): Promise<boolean> => {
	if (exchangeUnavailable) return false;
	if (pendingAcquire) return pendingAcquire;

	pendingAcquire = (async () => {
		try {
			const { token, user } = getUserData();
			if (!token || !user) return false;

			const roles = Array.isArray(user.role) ? user.role : [user.role].filter(Boolean);
			const role = mapCoreRoleToVesrRole(roles as string[]);

			const { data } = await axios.post(
				`${VESRAPI}/api/v1/vesr/auth/token`,
				{ role, name: user.FullName || '', email: user.Email || '' },
				{ headers: { Authorization: `Bearer ${token}` }, timeout: 10_000 }
			);

			if (!data?.token) return false;
			localStorage.setItem(VESR_TOKEN_KEY, data.token);
			if (data.expiresAt) {
				const expiresAtS =
					typeof data.expiresAt === 'number' ? data.expiresAt : Math.floor(new Date(data.expiresAt).getTime() / 1000);
				localStorage.setItem(VESR_TOKEN_EXPIRY_KEY, String(expiresAtS));
			}
			return true;
		} catch (error) {
			// Endpoint absent (production) or unreachable — callers fall back to
			// the core JWT, which production VESR accepts via the normalizer.
			if (axios.isAxiosError(error) && error.response?.status === 404) exchangeUnavailable = true;
			return false;
		} finally {
			pendingAcquire = null;
		}
	})();

	return pendingAcquire;
};

/**
 * The bearer to send to VESR: a valid VESR-issued token when we have one
 * (acquiring once if needed), else the core JWT (production path).
 */
export const resolveVesrBearer = async (): Promise<string> => {
	// An independent observer always sends the core JWT. The exchanged token carries only a role
	// and a name — no UserId, no ObserverId — so VESR could neither stamp their organisation on a
	// report nor route their support thread to their organisation's desk, and the role it names
	// ("Observer") is VESR's read-only console role, which refuses every report.
	if (ISOBSERVER()) return getUserData().token || '';
	// An Independent Supporter too: their own-reports-only scope and their linked elections live on
	// the core token, and api.vesr refuses the exchange for them (403 supporter_access_denied).
	if (ISSUPPORTER()) return getUserData().token || '';
	if (!isVesrTokenValid()) await acquireVesrToken();
	if (isVesrTokenValid()) return getVesrToken() || '';
	return getUserData().token || '';
};
